import { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Stack,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  IconButton,
  Button,
  TextField,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

const IMAGES_API_URL = "https://faromonitor.faroit.com.br:8080/images";

const ImageGalleryPage = () => {
  const [data, setData] = useState([]); // Mude para `useState<any[]>([])` se necessário
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imagesPerPage, setImagesPerPage] = useState(20); // Padrão: 20 imagens por página
  const [currentPage, setCurrentPage] = useState(1);
  const [imageUrls, setImageUrls] = useState({});
  const [openImage, setOpenImage] = useState(null);
  const [usernameFilter, setUsernameFilter] = useState(""); // Filtro para o nome de usuário

  // Função para ordenar as imagens por dataHora (crescente)
  const sortImagesByDate = (images) => {
    return images.sort((b, a) => new Date(a.dataHora) - new Date(b.dataHora));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(IMAGES_API_URL, {
          method: "GET",
          headers: {
            Authorization:
              "Bearer AEu49VoOZsqjAjtKW9j08bSL5Kb2ZAitUuKwWJx5l1Q0OeYDBFBYXErRUGYE7_iOJmI5qfHj9VIsDWBwA6aQxwrg",
          },
        });

        const json = await response.json();

        if (response.ok && json.sucesso === "SIM") {
          setData(json.data);
        } else {
          setError("Falha ao carregar as imagens.");
        }
      } catch (err) {
        setError("Erro ao tentar carregar as imagens. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchImageUrl = async (id) => {
    try {
      const response = await fetch(
        `https://faromonitor.faroit.com.br:8080/image/${id}`,
        {
          method: "GET",
          headers: {
            Authorization:
              "Bearer AEu49VoOZsqjAjtKW9j08bSL5Kb2ZAitUuKwWJx5l1Q0OeYDBFBYXErRUGYE7_iOJmI5qfHj9VIsDWBwA6aQxwrg",
          },
        }
      );
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      return null;
    }
  };

  // Pega as imagens da página atual, com filtro de username e ordenação por data
  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;

  const filteredData = usernameFilter
    ? data.filter((item) =>
        item.userName.toLowerCase().includes(usernameFilter.toLowerCase())
      )
    : data;

  const sortedData = sortImagesByDate(filteredData); // Ordena as imagens pela dataHora

  const currentImages = sortedData.slice(indexOfFirstImage, indexOfLastImage);

  useEffect(() => {
    const loadImages = async () => {
      const urls = {};
      for (const item of currentImages) {
        if (!imageUrls[item.id]) {
          urls[item.id] = await fetchImageUrl(item.id);
        }
      }
      setImageUrls((prev) => ({ ...prev, ...urls }));
    };

    if (currentImages.length > 0) {
      loadImages();
    }
  }, [currentImages, imageUrls]);

  const handleImagesPerPageChange = (event) => {
    setImagesPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleOpenImage = (imageUrl, index) => {
    setOpenImage({ url: imageUrl, index });
  };

  const handleCloseImage = () => {
    setOpenImage(null);
  };

  const handleNextImage = () => {
    const nextIndex = openImage.index + 1;
    if (nextIndex < sortedData.length) {
      const nextImage = sortedData[nextIndex];
      setOpenImage({ url: imageUrls[nextImage.id], index: nextIndex });
    }
  };

  const handlePreviousImage = () => {
    const prevIndex = openImage.index - 1;
    if (prevIndex >= 0) {
      const prevImage = sortedData[prevIndex];
      setOpenImage({ url: imageUrls[prevImage.id], index: prevIndex });
    }
  };

  const totalPages = Math.ceil(sortedData.length / imagesPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Lógica para gerar os números das páginas em volta da página atual
  const getPageNumbers = () => {
    const pageNumbers = [];
    const startPage = Math.max(1, currentPage - 3);
    const endPage = Math.min(totalPages, currentPage + 3);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Typography sx={{ textAlign: "center" }} variant="h4" gutterBottom>
        Galeria de Imagens
      </Typography>

      {error && (
        <Stack sx={{ width: "100%" }} spacing={1}>
          <Alert severity="error">{error}</Alert>
        </Stack>
      )}

      <Box sx={{ marginBottom: 2 }}>
        <Typography>Filtrar por Usuário:</Typography>
        <TextField
          value={usernameFilter}
          onChange={(e) => setUsernameFilter(e.target.value)}
          label="Nome de Usuário"
          variant="outlined"
          fullWidth
        />
      </Box>

      <Box sx={{ marginBottom: 2 }}>
        <Typography>Imagens por página:</Typography>
        <Select value={imagesPerPage} onChange={handleImagesPerPageChange}>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>
      </Box>

      <Grid container spacing={4}>
        {currentImages.map((item) => (
          <Grid item xs={12} sm={6} md={4} key={item.id}>
            <Card sx={{ boxShadow: 3 }}>
              <CardContent sx={{ backgroundColor: "#98add8" }}>
                <Typography variant="h6" sx={{ marginBottom: 2 }}>
                  {item.userName} - {item.computerName}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginBottom: 2 }}
                >
                  {new Date(item.dataHora).toLocaleString()}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  {imageUrls[item.id] ? (
                    <>
                      <img
                        src={imageUrls[item.id]}
                        alt={`Imagem ${item.id}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                      <IconButton
                        onClick={() =>
                          handleOpenImage(
                            imageUrls[item.id],
                            currentImages.indexOf(item)
                          )
                        }
                        sx={{
                          position: "absolute",
                          justifyContent: "center",
                          //   marginTop: "22%",
                          color: "#556197",
                          width: "10rem",
                          height: "10rem",
                        }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </>
                  ) : (
                    <CircularProgress />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Navegação entre páginas */}
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 3 }}>
        <Button onClick={handlePreviousPage} disabled={currentPage === 1}>
          Página Anterior
        </Button>
        {getPageNumbers().map((page) => (
          <Button
            key={page}
            onClick={() => setCurrentPage(page)}
            variant={page === currentPage ? "contained" : "outlined"}
          >
            {page}
          </Button>
        ))}
        <Button onClick={handleNextPage} disabled={currentPage === totalPages}>
          Próxima Página
        </Button>
      </Box>

      {/* Modal para exibir a imagem ampliada */}
      <Dialog open={!!openImage} onClose={handleCloseImage} maxWidth="md">
        <DialogContent sx={{ padding: 0 }}>
          {openImage && (
            <img
              src={openImage.url}
              alt="Imagem ampliada"
              style={{
                width: "100%",
                height: "auto",
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              //   padding: 1,
              backgroundColor: "#908d8d",
            }}
          >
            <Button
              onClick={handlePreviousImage}
              disabled={openImage?.index === 0}
            >
              Anterior
            </Button>
            <Button
              onClick={handleNextImage}
              disabled={openImage?.index === sortedData.length - 1}
            >
              Próxima
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImageGalleryPage;
